/**
 * Yandex.Metrica Service for Web Frontend
 * Handles event tracking and user identification
 */

declare global {
  interface Window {
    ym?: (id: number, method: string, ...args: any[]) => void;
  }
}

export class YandexMetricaService {
  private static readonly COUNTER_ID = 106954828;

  /**
   * Report custom event
   */
  static reportEvent(eventName: string, params?: Record<string, any>): void {
    if (typeof window !== 'undefined' && window.ym) {
      try {
        if (params) {
          window.ym(this.COUNTER_ID, 'reachGoal', eventName, params);
        } else {
          window.ym(this.COUNTER_ID, 'reachGoal', eventName);
        }
        console.log(`[Yandex.Metrica] Event reported: ${eventName}`, params);
      } catch (error) {
        console.error('[Yandex.Metrica] Error reporting event:', error);
      }
    }
  }

  /**
   * Set user ID for tracking
   */
  static setUserId(userId: number | string): void {
    if (typeof window !== 'undefined' && window.ym) {
      try {
        window.ym(this.COUNTER_ID, 'setUserID', String(userId));
        console.log(`[Yandex.Metrica] User ID set: ${userId}`);
      } catch (error) {
        console.error('[Yandex.Metrica] Error setting user ID:', error);
      }
    }
  }

  /**
   * Clear user ID
   */
  static clearUserId(): void {
    if (typeof window !== 'undefined' && window.ym) {
      try {
        window.ym(this.COUNTER_ID, 'setUserID', '');
        console.log('[Yandex.Metrica] User ID cleared');
      } catch (error) {
        console.error('[Yandex.Metrica] Error clearing user ID:', error);
      }
    }
  }

  /**
   * Report page view
   */
  static reportPageView(title?: string, url?: string): void {
    if (typeof window !== 'undefined' && window.ym) {
      try {
        const params: any = {};
        if (title) params.title = title;
        if (url) params.url = url;
        
        window.ym(this.COUNTER_ID, 'hit', url || window.location.href, params);
        console.log('[Yandex.Metrica] Page view reported');
      } catch (error) {
        console.error('[Yandex.Metrica] Error reporting page view:', error);
      }
    }
  }

  /**
   * Report error
   */
  static reportError(message: string, error?: Error): void {
    this.reportEvent('error', {
      message,
      stack: error?.stack,
      name: error?.name,
    });
  }

  /**
   * Report user action
   */
  static reportAction(action: string, details?: Record<string, any>): void {
    this.reportEvent(`user_${action}`, details);
  }
}

export default YandexMetricaService;
